import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { FaPhoneAlt, FaUser, FaMobileAlt } from "react-icons/fa";
import { HiMenu, HiX, HiMail } from "react-icons/hi";
import { BsGear } from "react-icons/bs";
import { BiWorld } from "react-icons/bi";
import { Link } from "react-scroll";
import SideNav from "./SideNav";

const logo = "https://res.cloudinary.com/edwinnetlify/image/upload/f_auto,q_auto/v1774918400/icon_vyamlc.png";
interface FormData {
  name: string;
  email: string;
  message: string;
  subject: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
  subject?: string;
}

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  // Contact form state
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: "",
    subject: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Form state

  const categories = [
    { id: "webapps", label: "WebApps", icon: <BsGear size={22} /> },
    { id: "apps", label: "Apps", icon: <FaMobileAlt size={22} /> },
    { id: "websites", label: "Web Sites", icon: <BiWorld size={22} /> },
    { id: "about", label: "About Me", icon: <FaUser size={20} /> },
  ];

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validate = (data: FormData): FormErrors => {
    let formErrors: FormErrors = {};
    if (!data.name) formErrors.name = "Name required";
    if (!data.email) formErrors.email = "Email required";
    if (!data.subject) formErrors.subject = "Subject is required";
    if (!data.message) formErrors.message = "Message is required";
    return formErrors;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErrors(validate(formData));
    setIsSubmitted(true);
  };

  const encode = (data: Record<string, string>) =>
    Object.keys(data)
      .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmitted) {
      fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact-form", ...formData as unknown as Record<string, string> }),
      })
        .then(() => alert("Success!"))
        .then(() => setIsSubmitted(false))
        .then(() => setFormData({ name: "", email: "", subject: "", message: "" }))
        .catch((error) => alert(error));
    }
  }, [errors, formData, isSubmitted]);

  // Close drawer when clicking outside
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        setContactOpen(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  // Scroll state for hiding navbar
  const [showNavbar, setShowNavbar] = useState(true);
  const lastScrollY = React.useRef(0);

  // Hide Navbar when scrolling down, show when scrolling up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      /* Only update once the page is scrolled down at least 50px */
      if (currentScrollY > lastScrollY.current && currentScrollY > 50) {
        setShowNavbar(false);
      } else if (currentScrollY < lastScrollY.current) {
        setShowNavbar(true);
      }
      
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      {/* ===== FIXED APP BAR ===== */}
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-white h-10 flex items-center px-4 shadow-md transition-transform duration-300 ease-in-out ${showNavbar ? "translate-y-0" : "-translate-y-full"}`}>
        {/* Menu Button */}
        <button
          onClick={() => setDrawerOpen(true)}
          className="text-gray-900 text-2xl mr-4 hover:opacity-80 transition-opacity"
          aria-label="Open menu"
        >
          <HiMenu />
        </button>

        {/* Right Side Icons */}
        <div className="ml-auto flex items-center gap-4">
          <a
            href="tel:+18322179892"
            className="text-gray-900 hover:opacity-80 transition-opacity"
            aria-label="Call"
          >
            <FaPhoneAlt size={16} />
          </a>
          <button
            onClick={() => setContactOpen(true)}
            className="text-gray-900 hover:opacity-80 transition-opacity"
            aria-label="Contact"
          >
            <HiMail size={22} />
          </button>
        </div>
      </nav>

      {/* <SideNav /> */}

      {/* ===== DRAWER BACKDROP ===== */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] transition-opacity"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* ===== DRAWER (Slide-in from left) ===== */}
      <aside
        className={`fixed top-0 left-0 h-full w-[240px] bg-teal-700 text-white font-['Montserrat'] z-[70] transform transition-transform duration-300 ease-in-out shadow-2xl ${drawerOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between bg-teal-800 h-16 px-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Logo" className="h-8 w-auto" crossOrigin="anonymous" width={32} height={32} />
            <span className="font-semibold text-sm">Menu</span>
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            className="text-white hover:opacity-80 text-xl"
            aria-label="Close menu"
          >
            <HiX />
          </button>
        </div>

        {/* Divider */}
        <div className="h-px bg-white/20" />

        {/* Nav Items */}
        <div className="py-6 px-4">
          <h3 className="text-white/40 text-[10px] font-bold uppercase tracking-[0.2em] mb-4 ml-2">Navigate</h3>
          <ul className="space-y-1">
            {categories.map((cat) => (
              <li key={cat.id}>
                <Link
                  to={cat.id}
                  spy={true}
                  smooth="easeInOutQuart"
                  offset={-100}
                  duration={1500}
                  onClick={() => setDrawerOpen(false)}
                  activeClass="bg-white/20 border-l-4 border-white"
                  className="w-full flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-white/10 transition-all text-left cursor-pointer group"
                >
                  <span className="text-white/70 group-hover:text-white transition-colors">{cat.icon}</span>
                  <span className="font-medium text-sm">{cat.label}</span>
                </Link>
              </li>
            ))}
          </ul>

          <div className="h-px bg-white/10 my-6 mx-2" />

          <h3 className="text-white/40 text-[10px] font-bold uppercase tracking-[0.2em] mb-4 ml-2">Connect</h3>
          <ul className="space-y-1">
            <li>
              <a
                href="tel:+18322179892"
                className="flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-white/10 transition-all text-white no-underline group"
              >
                <div className="text-white/70 group-hover:text-white transition-colors">
                  <FaPhoneAlt size={18} />
                </div>
                <span className="font-medium text-sm">Let's have a call</span>
              </a>
            </li>
          </ul>
        </div>
      </aside>

      {/* ===== CONTACT MODAL ===== */}
      {contactOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setContactOpen(false)}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-8 z-10 animate-[fadeIn_0.2s_ease-out]">
            <button
              onClick={() => setContactOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl transition-colors"
              aria-label="Close contact form"
            >
              <HiX />
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-6 font-['Montserrat']">
              Contact
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  autoComplete="name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  autoComplete="off"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                />
                {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  autoComplete="off"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none"
                />
                {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
              </div>

              <button
                type="submit"
                className="w-full bg-teal-900 hover:bg-teal-950 text-white font-semibold py-3 rounded-lg transition-colors duration-300"
              >
                Send
              </button>
              <p className="text-gray-400 text-sm italic text-center">
                Emails normally are responded to in 24 hrs
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
